import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import LocationFormDialog from "@/components/LocationFormDialog";

/**
 * Two-tab dense spreadsheet directory (Customers = pickup, Consignees = delivery).
 * Green-banded rows, sortable headers, sticky top row — dispatcher-friendly.
 */
export default function AddressDirectory() {
  const [tab, setTab] = useState("customers");
  return (
    <div className="p-6 space-y-4">
      <div>
        <div className="mono-label">DIRECTORY</div>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Customers &amp; Consignees</h1>
        <p className="text-sm text-zinc-600 mt-1">
          Central address book. Every pickup / delivery you save here is one-click reusable inside the Job form.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="sharp bg-zinc-100 p-0 h-auto border border-zinc-950">
          <TabsTrigger value="customers" className="sharp px-6 py-2 text-sm data-[state=active]:bg-[#0056B3] data-[state=active]:text-white" data-testid="tab-customers">
            CUSTOMERS · Pickup
          </TabsTrigger>
          <TabsTrigger value="consignees" className="sharp px-6 py-2 text-sm data-[state=active]:bg-[#0056B3] data-[state=active]:text-white" data-testid="tab-consignees">
            CONSIGNEES · Delivery
          </TabsTrigger>
        </TabsList>
        <TabsContent value="customers" className="mt-4">
          <DirectoryGrid kind="pickup" title="Customers" />
        </TabsContent>
        <TabsContent value="consignees" className="mt-4">
          <DirectoryGrid kind="delivery" title="Consignees" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const COLS = [
  { key: "label",         label: "Name",    w: 260 },
  { key: "address",       label: "Address", w: 480 },
  { key: "contact_name",  label: "Contact", w: 180 },
  { key: "contact_phone", label: "Phone",   w: 150 },
];

function DirectoryGrid({ kind, title }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ key: "label", dir: "asc" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations", { params: { kind } });
      const shared = (data || []).filter((l) => !l.customer_id);
      setRows(shared);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const enriched = useMemo(() => rows.map((r) => ({
    ...r,
    address: [r.street, r.city, r.state, r.zip, r.country].filter(Boolean).join(", "),
  })), [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = enriched;
    if (s) out = out.filter((r) => {
      const blob = [r.label, r.name, r.address, r.contact_name, r.contact_phone, r.email]
        .filter(Boolean).join(" ").toLowerCase();
      return blob.includes(s);
    });
    out = [...out].sort((a, b) => {
      const av = (a[sort.key] || "").toString().toLowerCase();
      const bv = (b[sort.key] || "").toString().toLowerCase();
      if (av === bv) return 0;
      const c = av < bv ? -1 : 1;
      return sort.dir === "asc" ? c : -c;
    });
    return out;
  }, [enriched, q, sort]);

  const totalW = COLS.reduce((a, c) => a + c.w, 0) + 60;

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.label}"?`)) return;
    try {
      await api.delete(`/locations/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${title.toLowerCase()} by name, OrgID, city, phone…`}
            className="sharp pl-8 h-9"
            data-testid={`dir-search-${kind}`}
          />
        </div>
        <div className="text-xs text-zinc-500 font-mono">{filtered.length} record{filtered.length === 1 ? "" : "s"}</div>
        <Button
          onClick={() => { setEditing(null); setOpenForm(true); }}
          className="sharp bg-[#0056B3] hover:bg-[#004494] h-9"
          data-testid={`dir-add-${kind}`}
        >
          <Plus className="h-4 w-4 mr-1" /> New {title.slice(0, -1)}
        </Button>
      </div>

      <div className="apex-grid-wrap">
        <table className="apex-grid" style={{ width: totalW }}>
          <thead>
            <tr>
              <th
                className="sticky left-0 z-20"
                style={{ width: 60, minWidth: 60, background: "#f2f2f2" }}
              >&nbsp;</th>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  className="cursor-pointer hover:bg-zinc-200"
                  style={{ width: c.w, minWidth: c.w }}
                  onClick={() => toggleSort(c.key)}
                  data-testid={`dir-col-${c.key}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {sort.key === c.key && <ArrowUpDown className="h-3 w-3" />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={COLS.length + 1} className="px-3 py-6 text-center text-zinc-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={COLS.length + 1} className="px-3 py-6 text-center text-zinc-500">
                {q ? "No matches." : `No ${title.toLowerCase()} yet.`}
              </td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`dir-row-${r.id}`}>
                <td
                  className="text-center sticky left-0 z-[1]"
                  style={{ width: 60, minWidth: 60, background: "inherit" }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => { setEditing(r); setOpenForm(true); }}
                      className="p-0.5 hover:text-[#0056B3]"
                      title="Edit"
                      data-testid={`dir-edit-${r.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => remove(r)}
                      className="p-0.5 text-zinc-600 hover:text-rose-700"
                      title="Delete"
                      data-testid={`dir-delete-${r.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </td>
                {COLS.map((c) => (
                  <td key={c.key} style={{ maxWidth: c.w }}>
                    {r[c.key] || ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LocationFormDialog
        open={openForm}
        onOpenChange={setOpenForm}
        kind={kind}
        location={editing}
        onSaved={load}
      />
    </div>
  );
}
